import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL as BASE_URL } from '../services/apiBaseUrl';
import { TENANT_SUBDOMAIN } from '../utils/tenantHost';

const TenantContext = createContext(null);

// status:
//   'loading'   – fetching the tenant list
//   'ready'     – on a tenant subdomain that matched an active tenant
//   'root'      – on the root domain (no tenant) → platform landing page
//   'not-found' – on a subdomain that matches no active tenant
//   'error'     – tenant list could not be loaded on a tenant subdomain
//
// ---------------------------------------------------------------------------
// TenantProvider
// - The tenant comes only from the hostname (see utils/tenantHost.js), which
//   is read once at boot. There is no in-app tenant switching: another tenant
//   lives on another origin.
// - The matched tenant's id is written to localStorage so services/api.js can
//   send it as X-Tenant-ID. It is written before children render, so no API
//   call can go out with a missing or stale tenant id.
// ---------------------------------------------------------------------------
export function TenantProvider({ children }) {
  const [tenant,  setTenant]  = useState(null);
  const [tenants, setTenants] = useState([]);
  const [status,  setStatus]  = useState('loading');

  useEffect(() => {
    axios.get(`${BASE_URL}/tenants`)
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setTenants(list);

        if (!TENANT_SUBDOMAIN) {
          localStorage.removeItem('tenantId');
          setStatus('root');
          return;
        }

        // Match on `subdomain`, the same field the backend resolves from the
        // Host header, so client and server can never disagree.
        const match = list.find((t) => t.subdomain === TENANT_SUBDOMAIN);
        if (!match) {
          localStorage.removeItem('tenantId');
          setStatus('not-found');
          return;
        }

        localStorage.setItem('tenantId', match.id);
        setTenant(match);
        setStatus('ready');
      })
      .catch(() => {
        localStorage.removeItem('tenantId');
        // The landing page is still useful without the clinic list.
        setStatus(TENANT_SUBDOMAIN ? 'error' : 'root');
      });
  }, []);

  if (status === 'loading') {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontFamily: 'system-ui, sans-serif', color: '#6366f1',
      }}>
        Loading…
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        fontFamily: 'system-ui, sans-serif', gap: '8px',
      }}>
        <div style={{ fontSize: '18px', fontWeight: 600, color: '#dc2626' }}>Error</div>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>Could not load clinic details. Please check your connection.</div>
      </div>
    );
  }

  return (
    <TenantContext.Provider value={{ tenant, tenants, status, subdomain: TENANT_SUBDOMAIN }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) return { tenant: null, tenants: [], status: 'root', subdomain: null };
  return ctx;
}
